<template>
  <div class="sample-sidepanel px-4">
    <b-row class="d-flex align-items-center mb-2">
      <b-col class="d-flex align-items-center">
        <span class="ap-title ap-title__main"><i
          class="far fa-prescription-bottle mr-2"
          aria-hidden="true"
        />{{ samLabel }}</span>
        <div
          v-if="process != null"
          class="ml-4"
        >
          <i
            class="fas fa-tint main"
            aria-hidden="true"
          />
          <span class="mx-2">{{ process.description }}</span>
        </div>
      </b-col>
      <b-col
        cols="auto"
        class="d-flex justify-content-end"
      >
        <div>
          <b-button
            v-shortkey="['shift', 'esc']"
            class="btn__icon btn__icon--primary p-0"
            @shortkey="closeSampleSidepanel"
            @click="closeSampleSidepanel"
          >
            <i
              class="far fa-times"
              aria-hidden="true"
            />
          </b-button>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <b-tabs
          v-model="activeTabIndex"
          pills
          card
          vertical
          class="vt-tabs--vertical"
        >
          <b-tab @click="loadMacro">
            <template slot="title">
              <i
                class="far fa-align-justify"
                aria-hidden="true"
              />
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center my-2 pt-1">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.macroscopy') }}</span>
                </b-col>
              </b-row>
              <b-row class="content-scroll">
                <b-col class="mt-2">
                  <vt-rich-text-editor
                    input-id="descriptionDescriptionInput"
                    class="mt-1 descriptionText"
                    :value=" macro ? macro.text : ''"
                    :disabled="true"
                  />
                </b-col>
              </b-row>
            </div>
          </b-tab>
          <b-tab @click="loadMicro">
            <template slot="title">
              <i
                class="fal fa-microscope"
                aria-hidden="true"
              />
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center my-2 pt-1">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.microscopy') }}</span>
                </b-col>
              </b-row>
              <b-row class="content-scroll">
                <b-col class="mt-2">
                  <vt-rich-text-editor
                    input-id="descriptionDescriptionInput"
                    class="mt-1 descriptionText"
                    :value="micro ? micro.text : ''"
                    :disabled="true"
                  />
                </b-col>
              </b-row>
            </div>
          </b-tab>
          <b-tab @click="loadDiagnosis">
            <template slot="title">
              <i
                class="fal fa-comment-alt-medical"
                aria-hidden="true"
              />
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center my-2 pt-1">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.diagnosis') }}</span>
                </b-col>
              </b-row>
              <b-row class="content-scroll">
                <b-col class="mt-2">
                  <vt-rich-text-editor
                    input-id="descriptionDescriptionInput"
                    class="mt-1 descriptionText"
                    :value="diagnosis ? diagnosis.text : ''"
                    :disabled="true"
                  />
                </b-col>
              </b-row>
            </div>
          </b-tab>
          <b-tab @click="loadAdditionalReport">
            <template slot="title">
              <i
                class="fal fa-files-medical"
                aria-hidden="true"
              />
            </template>
            <div class="ml-2">
              <b-row class="d-flex align-items-center my-2 pt-1">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.additionalReport') }}</span>
                </b-col>
              </b-row>
              <b-row class="content-scroll">
                <b-col class="mt-2">
                  <vt-rich-text-editor
                    input-id="descriptionDescriptionInput"
                    class="mt-1 descriptionText"
                    :value="additionalReport ? additionalReport.text : ''"
                    :disabled="true"
                  />
                </b-col>
              </b-row>
            </div>
          </b-tab>
          <b-tab @click="loadCodifications">
            <template slot="title">
              <i
                class="fal fa-project-diagram"
                aria-hidden="true"
              />
            </template>
            <div class="content-scroll ml-2">
              <b-row class="d-flex align-items-center my-2 pt-1">
                <b-col>
                  <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.codifications') }}</span>
                </b-col>
              </b-row>
              <div v-if="codifications.length">
                <b-row class="mt-2 mb-3 header-row">
                  <b-col>{{ $t('diagnosis.labels.topographic') }}</b-col>
                  <b-col>{{ $t('diagnosis.labels.morphological') }}</b-col>
                </b-row>
                <b-row
                  v-for="codification in codifications"
                  :key="codification.id"
                  class="codifications-row"
                >
                  <b-col>{{ formatTopographicCode(codification.topographicCode) }}</b-col>
                  <b-col>{{ formatMorphologicCode(codification.morphologicCode) }}</b-col>
                  <b-col cols="12">
                    <hr class="my-2">
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-tab>
          <b-tab @click="loadAssaysData">
            <template slot="title">
              <i
                class="fas fa-flask"
                aria-hidden="true"
              />
            </template>
            <div
              v-if="assaysData"
              class="techniques-tab"
            >
              <b-row class="content-scroll">
                <b-col>
                  <b-tabs
                    class="vt-tabs sr-detail-tabs"
                    content-class="mt-1"
                  >
                    <b-tab
                      v-for="(tab, index) in tabs"
                      :key="index"
                    >
                      <template slot="title">
                        <div class="d-flex align-items-center">
                          <i
                            v-tooltip="tab.title"
                            :class="tab.icon"
                            aria-hidden="true"
                          />
                          <vtBubble
                            v-if="assaysData[tab.state].length"
                            :number="assaysData[tab.state].length"
                            :info="true"
                            class="ml-1"
                          />
                        </div>
                      </template>
                      <div class="px-3 scroll-area">
                        <b-row class="mb-2 mt-3">
                          <b-col>
                            <span class="ap-title ap-title__section">{{ assaysData[tab.state].length }} {{ tab.title }}</span>
                          </b-col>
                        </b-row>
                        <b-row
                          v-for="(assay, indexAssay) in assaysData[tab.state]"
                          :key="indexAssay"
                          class="tech-row bottom-bordered py-2"
                        >
                          <b-col
                            cols="auto"
                            class="d-flex align-items-center"
                          >
                            <div
                              v-tooltip="assay.assayStatus.name"
                              class="mr-2 state-circle"
                              :class="{'state-pending': assay.assayStatus.code === 'PENDING',
                                       'state-process-1': assay.assayStatus.code === 'IN_PROCESS',
                                       'state-canceled': assay.assayStatus.code === 'CANCELED',
                                       'state-success': assay.assayStatus.code === 'FINISHED'
                              }"
                            />
                            <span class="dark">{{ assay.label }}</span>
                            <priority-dot
                              class="ml-3"
                              :priority="assay.priority"
                              :size="10"
                            />
                          </b-col>
                          <b-col>
                            <span class="dark">{{ assay.assay ? `${assay.assay.name} - ${assay.assay.assaygroup.name}` : '' }}</span>
                          </b-col>
                        </b-row>
                      </div>
                    </b-tab>
                  </b-tabs>
                </b-col>
              </b-row>
            </div>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import sampleMacroService from '@/api/resources/description/SampleMacroService'
import sampleMicroService from '@/api/resources/description/SampleMicroService'
import sampleAdditionalReportService from '@/api/resources/description/SampleAdditionalReportService'
import sampleDiagnosisService from '@/api/resources/description/SampleDiagnosisService'
import sampleCodificationService from '@/api/resources/common/sampleCodificationServices'
import subportionService from '@/modules/diagnosisModule/services/subportionService'
import PriorityDot from '@/modules/clinicalModule/components/StatusRequestPriorityDot'

const MACRO_TAB_INDEX = 0
const MICRO_TAB_INDEX = 1
const DIAGNOSIS_TAB_INDEX = 2
const ADDITIONAL_REPORT_TAB_INDEX = 3
const CODIFICATIONS_TAB_INDEX = 4
const ASSAYS_TAB_INDEX = 5
export default {
    name: 'SampleSidepanel',
    components: {
        VtBubble,
        PriorityDot
    },
    data() {
        return {
            activeTabIndex: MACRO_TAB_INDEX,
            samId: null,
            casId: null,
            labId: null,
            procedure: null,
            process: null,
            samLabel: null,
            macro: null,
            micro: null,
            diagnosis: null,
            additionalReport: null,
            codifications: [],
            assaysData: null,
            mockupValue: 2,
            tabs: [
                {
                    icon: 'fas fa-arrow-circle-right',
                    title: this.$t('diagnosis.grid.caseGrid.requestedTechniques'),
                    state: 'requested'
                },
                {
                    icon: 'fal fa-cut',
                    title: this.$t('diagnosis.grid.caseGrid.pendingCutTechniques'),
                    state: 'pendingCut'
                },
                {
                    icon: 'fal fa-paint-brush-alt',
                    title: this.$t('diagnosis.grid.caseGrid.pendingDyeingTechniques'),
                    state: 'pendingStain'
                },
                {
                    icon: 'fal fa-envelope',
                    title: this.$t('diagnosis.grid.caseGrid.pendingDeliveryTechniques'),
                    state: 'pendingDeliveryToPathologistOrScan'
                },
                {
                    icon: 'fal fa-times-circle',
                    title: this.$t('diagnosis.grid.caseGrid.canceledTechniques'),
                    state: 'canceled'
                }
            ]
        }
    },
    methods: {
        closeSampleSidepanel() {
            this.$emit('closeSampleSidepanel', false)
        },
        formatTopographicCode(topographicCode) {
            if (!topographicCode) {
                return ''
            }
            return `${topographicCode.code} | ${topographicCode.description}`
        },
        formatMorphologicCode(morphologicCode) {
            if (!morphologicCode) {
                return ''
            }
            return `${morphologicCode.code} | ${morphologicCode.description}`
        },
        loadSidePanelData(diagnosisSample, labId) {
            this.casId = diagnosisSample.caseStudyId
            this.samId = diagnosisSample.id
            this.samLabel = diagnosisSample.samLabel
            this.labId = labId
            this.procedure = diagnosisSample.procedure?.description
            this.process = diagnosisSample.sampleProcess
            this.updateLoading(true)
            switch (this.activeTabIndex) {
            case MACRO_TAB_INDEX:
                this.loadMacro()
                break
            case MICRO_TAB_INDEX:
                this.loadMicro()
                break
            case DIAGNOSIS_TAB_INDEX:
                this.loadDiagnosis()
                break
            case ADDITIONAL_REPORT_TAB_INDEX:
                this.loadAdditionalReport()
                break
            case CODIFICATIONS_TAB_INDEX:
                this.loadCodifications()
                break
            case ASSAYS_TAB_INDEX:
                this.loadAssaysData()
                break
            }
        },
        async loadMacro() {
            const [err, response] = await this.toCall(sampleMacroService.findById(this.labId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.macro = response
        },
        async loadMicro() {
            const [err, response] = await this.toCall(sampleMicroService.findById(this.labId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.micro = response
        },
        async loadDiagnosis() {
            const [err, response] = await this.toCall(sampleDiagnosisService.findById(this.labId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.diagnosis = response
        },
        async loadAdditionalReport() {
            const [err, response] = await this.toCall(sampleAdditionalReportService.findById(this.labId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.additionalReport = response
        },
        async loadCodifications() {
            const [err, response] = await this.toCall(sampleCodificationService.getSampleCodifications(this.labId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.codifications = response
        },
        async loadAssaysData() {
            const [err, response] = await this.toCall(subportionService.getAssaySummaryForSample(this.labId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.assaysData = response
        }
    }
}
</script>

<style lang="scss" scoped>
    .sample-sidepanel .title {
        font-size: font-size(big);
        color: vt-color(primary);
    }

    .sample-sidepanel .tech-row {
        border-bottom: 1px solid rgb(209, 209, 209);
        font-size: font-size(small);
    }

    .sample-sidepanel .scroll-area{
        height: calc(100vh - 210px);
        overflow-y: auto;
        overflow-x: hidden;
    }

    @media (max-width: 1300px) {
        .sample-sidepanel .title {
            font-size: font-size(normal);
            color: vt-color(primary);
        }
    }
</style>

<style lang="scss">
    .sample-sidepanel .descriptionText .ql-toolbar {
        display: none;
    }
    .sample-sidepanel .descriptionText .ql-container {
        border-top: 1px solid #ccc !important;
        word-break: break-word;
    }
    .sample-sidepanel .descriptionText .ql-editor {
        height: calc(100vh - 310px);
    }

    .sample-sidepanel .tabs.row {
        height: calc(100vh - 310px) !important;
        border: 1px solid rgba(0, 0, 0, 0.125);
    }

    .sample-sidepanel .showtooltip {
        display: block;
    }

    .sample-sidepanel .noshowtooltip {
        display: none;
    }

    .techniques-tab .vt-tabs .nav-tabs .nav-link.active,
    .vt-tabs--small .nav-tabs .nav-link.active {
        border-bottom: 2px solid vt-color(primary);
    }

    .techniques-tab .vt-tabs .nav-tabs .nav-link,
    .vt-tabs--small .nav-tabs .nav-link {
        border-bottom: 2px solid transparent;
    }

    .techniques-tab .vt-tabs .nav-tabs .nav-link,
    .vt-tabs--small .nav-tabs .nav-link i {
        font-size: font-size(very-normal) !important;
    }

    .sample-sidepanel .techniques-tab .nav-link.active::before {
        background: transparent;
    }

    .sample-sidepanel .content-scroll {
        height: calc(100vh - 160px);
        overflow-y: auto;
        overflow-x: hidden;
    }

    .sample-sidepanel .tabs.row {
        height: calc(100vh - 150px) !important;
    }

    @media (max-width: 1300px) {
        .sample-sidepanel .nav-link {
            width: 50px;
        }

        .sample-sidepanel .techniques-tab .nav-link {
            width: auto;
        }

        .sample-sidepanel .content-scroll {
            height: calc(100vh - 150px);
        }

        .sample-sidepanel .tabs.row {
            height: calc(100vh - 140px) !important;
        }
    }
</style>
